import { useMutation } from 'react-query';

import {
  confirmLoginVerificationSession,
  confirmSignupVerificationSession,
  createLoginVerificationSession,
  createSignupVerificationSession,
  emailLogin,
  emailRegister,
  phoneLogin,
  phoneLoginRequest,
  phoneRegister,
  resendLoginVerificationCode,
  resendVerificationEmail,
  resetPasswordRequest,
  updateLoginVerificationSession,
  verifyEmail
} from './service';

import { ErrorResponse } from 'legacy/common/utils/error';

import {
  LoginRequest,
  LoginResponse,
  PhoneLoginRequest,
  PhoneLoginRequestResponse,
  RegisterRequest,
  VerificationSessionDto
} from './types';
import { GenericSuccessResponse } from 'legacy/common/types/response';

export function useEmailLoginCommand() {
  return useMutation<LoginResponse, ErrorResponse, LoginRequest>(emailLogin);
}

export function useEmailRegisterCommand() {
  return useMutation<LoginResponse, ErrorResponse, RegisterRequest>(
    emailRegister
  );
}

export function usePhoneLoginRequestCommand() {
  return useMutation<PhoneLoginRequestResponse, ErrorResponse, string>(
    phoneLoginRequest
  );
}

export function usePhoneLoginCommand() {
  return useMutation<LoginResponse, ErrorResponse, PhoneLoginRequest>(
    phoneLogin
  );
}

export function usePhoneRegisterCommand() {
  return useMutation<LoginResponse, ErrorResponse, PhoneLoginRequest>(
    phoneRegister
  );
}

export function useResetPasswordRequestCommand() {
  return useMutation<void, ErrorResponse, string>(resetPasswordRequest);
}

export function useResendVerificationEmailCommand() {
  return useMutation<void, ErrorResponse, string>(resendVerificationEmail);
}

export function useCreateSignupVerificationSessionCommand() {
  return useMutation<
    VerificationSessionDto,
    ErrorResponse,
    {
      phone?: string;
      countryCode?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      country?: string;
      password?: string;
    }
  >(createSignupVerificationSession);
}

export function useConfirmSignupVerificationSessionCommand() {
  return useMutation<
    LoginResponse,
    ErrorResponse,
    {
      verificationSessionId: string;
      password?: string;
      smsCode?: string;
      mobileDeviceId?: string;
    }
  >(confirmSignupVerificationSession);
}

export function useCreateLoginVerificationSessionCommand() {
  return useMutation<
    VerificationSessionDto | { isLegacyAuthUser: boolean },
    ErrorResponse,
    {
      phoneNumber?: string;
      email?: string;
      countryCode?: string;
      requireOtp?: boolean;
    }
  >(createLoginVerificationSession);
}

export function useUpdateLoginVerificationSessionCommand() {
  return useMutation<
    VerificationSessionDto,
    ErrorResponse,
    {
      password: string;
      verificationSessionId: string;
    }
  >(updateLoginVerificationSession);
}

export function useConfirmLoginVerificationSessionCommand() {
  return useMutation<
    LoginResponse,
    ErrorResponse,
    {
      verificationSessionId: string;
      smsCode?: string;
    }
  >(confirmLoginVerificationSession);
}

export function useResendLoginVerificationCodeCommand() {
  return useMutation<
    GenericSuccessResponse,
    ErrorResponse,
    {
      verificationSessionId: string;
      deliveryMethod?: 'sms' | 'email';
    }
  >(resendLoginVerificationCode);
}

export function useVerifyEmailCommand() {
  return useMutation<{ success: boolean }, ErrorResponse, string>(verifyEmail);
}
